/**
 * mobile-api 전용 axios 클라이언트.
 *
 * onl-api(payment의 `api` 인스턴스)와 분리한다. mobile-api는:
 *  - 응답 envelope이 없다 — 응답 바디가 곧 데이터 객체
 *  - 에러도 NestJS 기본 형식({ statusCode, message })이라 onl-api의 ApiError 와 다름
 *
 * 따라서 onl-api용 envelope unwrap·ApiError 파싱 인터셉터를 적용하면 안 된다.
 * 인증만 공유 — tokenStore의 토큰을 Authorization: Bearer 헤더로 주입한다.
 *
 * 토큰 만료(HTTP 401)는 onl-api 와 동일하게 브릿지에서 토큰 갱신 후 원본 요청 1회 재시도한다.
 * (envelope 가 없어 status:401 body 케이스는 없고 실 HTTP 401 만 처리)
 */

import axios from 'axios'

import { API_TIMEOUT_MS, MOBILE_API_BASE_URL } from '@/constants'

import { getToken, refreshTokenFromBridge } from './tokenStore'

/** 401 자동 재시도 1회 플래그 — 무한 루프 방지 (onl-api `api` 와 동일 규약) */
interface MobileRequestWithRetry {
  _tokenRetried?: boolean
}

const TOKEN_EXPIRED_STATUS = 401

const mobileApi = axios.create({
  baseURL: MOBILE_API_BASE_URL,
  timeout: API_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
})

mobileApi.interceptors.request.use((config) => {
  const token = getToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

mobileApi.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (axios.isAxiosError(error)) {
      const reqConfig = error.config as (typeof error.config & MobileRequestWithRetry) | undefined
      // HTTP 401 (토큰 만료) → 브릿지에서 토큰 갱신 후 원본 요청 1회 재시도
      if (error.response?.status === TOKEN_EXPIRED_STATUS && reqConfig && !reqConfig._tokenRetried) {
        reqConfig._tokenRetried = true
        const refreshed = await refreshTokenFromBridge()
        if (refreshed) return mobileApi.request(reqConfig)
      }
    }
    return Promise.reject(error)
  }
)

export default mobileApi
